/* React base class */
import { Component, createRef } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router and shared utilities */
import { withRouter, utils, applySeo } from "../helpers"

/* UI components */
import { Loading, Network } from "../components"

/* Icon component */
import Icon from "../components/Icon"

/* Organization API calls */
import { get, services as orgServices, specialists as orgSpecialists, availability } from "../api/Organization"

/* Booking API call */
import { create } from "../api/Booking"

/* Queue API call */
import { join } from "../api/Queue"

/* i18n */
import { translate } from "../translates"

/* Short weekday labels */
const DOW = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

/* Short month labels */
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]

/* Page Booking */
class Booking extends Component {

    constructor(props) {
        super(props)
        /* State */
        this.state = {
            loading: true,
            error: false,
            organization: null,
            services: [],
            specialists: [],
            /* Which of the dates ahead have an hour to offer at all. A day with none of them is
               not shown: an empty «выберите время» tells the client nothing */
            days: [],
            slots: [],
            specialist: null,
            dateIdx: 0,
            time: null,
            submitting: false,
            success: false,
            submitError: null,
            /* «Записаться» was pressed before an hour was picked — the hours are lit up */
            timeMissing: false
        }

        /* The hours of the date — the button brings the client to them when none is picked */
        this._times = createRef()
    }

    /* Turn a booking response into a human-readable Russian error, or null when it succeeded */
    _submitError = (response) => {
        if (!response) {
            return "Нет соединения с сервером. Проверьте интернет и попробуйте ещё раз."
        }
        if (response.status === 201 || response.status === 200) {
            return null
        }
        if (response.status === 409 && response.data && response.data.error && response.data.error.code === "SERVICE_NOT_PROVIDED") {
            return "Этот мастер не оказывает выбранную услугу. Выберите другого."
        }
        if (response.status === 409) {
            return "Это время только что заняли. Выберите другое."
        }
        if (response.status === 401 || response.status === 498) {
            /* Handled by _sessionExpired, which opens the login modal and sets its own message */
            return null
        }
        if (response.status === 400) {
            return "Проверьте выбранные услугу, дату и время."
        }
        return "Не удалось создать запись. Попробуйте позже."
    }

    /* Draw Dates */
    _dates = () => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date()
            d.setDate(d.getDate() + i)
            /* Format in local time — toISOString() would shift to UTC and roll the date back in Almaty */
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
            return { iso, wd: d.getDay(), dow: DOW[d.getDay()], day: d.getDate(), mon: MONTHS[d.getMonth()] }
        })
    }

    /* Weekday indices (JS getDay) the organization is closed, from its schedule */
    /* Draw Off Days */
    _offDays = (org) => {
        const source = org || this.state.organization
        const schedule = source && source.hours ? source.hours.schedule : []
        const MAP = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 }
        const off = new Set()
        ;(schedule || []).forEach((entry) => { if (entry.dayOff) off.add(MAP[entry.day]) })
        return off
    }

    /* Draw Service Ids */
    _serviceIds = () => {
        const search = new URLSearchParams(this.props.location.search)
        const raw = search.get("services") || ""
        return raw ? raw.split(",").filter((item) => item) : []
    }

    componentDidMount = () => {
        /* SEO */
        applySeo({
            title: "Оформление записи — Bookify",
            description: "Оформление онлайн-записи в Bookify.",
            robots: "noindex, nofollow",
            path: "/booking"
        })
        this.load()
    }

    load = () => {
        const search = new URLSearchParams(this.props.location.search)
        const id = search.get("organization")
        this.setState({ loading: true, error: false })

        Promise.all([get(id), orgServices(id), orgSpecialists(id, this._serviceIds())]).then(([resOrg, resServices, resSpec]) => {
            if (resOrg && resOrg.status === 200) {
                const ids = this._serviceIds()
                /* Services API returns either a flat array (legacy) or { serviceType, services, groups } */
                const serviceData = resServices && resServices.status === 200 ? resServices.data : null
                const all = serviceData ? (Array.isArray(serviceData) ? serviceData : (serviceData.services || [])) : []
                const specs = resSpec && resSpec.status === 200 ? resSpec.data : []
                /* Start on the first working day, skipping the organization's days off */
                const off = this._offDays(resOrg.data)
                const firstWorking = this._dates().findIndex((date) => !off.has(date.wd))
                this.setState({
                    organization: resOrg.data,
                    services: all.filter((svc) => ids.includes(`${svc.id}`)),
                    specialists: specs,
                    specialist: specs.length > 0 ? specs[0].id : null,
                    dateIdx: firstWorking >= 0 ? firstWorking : 0,
                    loading: false
                }, this.availability)
            }
            else {
                this.setState({ loading: false, error: true })
            }
        })
    }

    /* The hours of the chosen date, and which of the dates ahead have any hours at all. Both
       come in one answer, so the strip of the days never asks separately */
    availability = () => {
        if (!this.state.organization || this.state.organization.queue) {
            return
        }
        const dates = this._dates()
        const date = dates[this.state.dateIdx].iso
        availability(this.state.organization.id, { serviceIds: this._serviceIds().join(","), specialistId: this.state.specialist, date, days: dates.length }).then((response) => {
            if (response && response.status === 200) {
                const days = Array.isArray(response.data.days) ? response.data.days : []
                this.setState({ slots: response.data.slots || [], days, time: null }, this._keepDay)
            }
        })
    }

    /* The date which is open must be one of those the strip still shows. A master who does not
       work the day the page opened at moves the client to his nearest one */
    _keepDay = () => {
        /* Fields */
        const { days, dateIdx } = this.state
        const dates = this._dates()

        /* The answer said nothing about the days — the strip stays as it is */
        if (days.length === 0) {
            return
        }

        const open = (iso) => days.some((item) => item.date === iso && item.available)

        /* The date which is open is still worth standing on */
        if (dates[dateIdx] && open(dates[dateIdx].iso)) {
            return
        }

        const next = dates.findIndex((item) => open(item.iso))

        /* Nowhere to move to — the master has no hours in the whole week */
        if (next === -1 || next === dateIdx) {
            return
        }

        this.setState({ dateIdx: next }, this.availability)
    }

    /* The dates the strip shows: the ones the master really has hours on. Until the first
       answer arrives the days off of the point are the best guess there is */
    _openDates = () => {
        /* Fields */
        const { days } = this.state
        const dates = this._dates()

        if (days.length === 0) {
            const off = this._offDays()
            return dates.filter((item) => !off.has(item.wd))
        }

        return dates.filter((item) => days.some((day) => day.date === item.iso && day.available))
    }

    /* The master has no free hour in the whole week ahead. The advice depends on whether there
       is anybody else to go to: a point with one master can only be waited for */
    _noDates = () => {
        /* Fields */
        const { specialists } = this.state

        return (
            <div className="slots-empty">

                <div className="slots-empty__art">
                    <Icon name="calendar" size={20} />
                </div>

                <div className="slots-empty__body">

                    <div className="slots-empty__title">Свободного времени нет</div>

                    <div className="slots-empty__text">
                        {specialists.length > 1
                            ? "На ближайшую неделю мастер занят. Выберите другого специалиста — у него может быть свободнее"
                            : "На ближайшую неделю мастер занят. Загляните позже или позвоните в заведение"
                        }
                    </div>

                </div>

            </div>
        )
    }

    /* The strip shows a part of the week, so the date is found by itself */
    _pickDate = (date) => {
        /* Fields */
        const index = this._dates().findIndex((item) => item.iso === date.iso)

        if (index === -1) {
            return
        }

        this.setState({ dateIdx: index }, this.availability)
    }

    /* Draw Close */
    _close = () => {
        if (this.state.organization) {
            /* Carry the chosen services back so they stay selected on the organization page */
            const ids = this._serviceIds().join(",")
            const query = ids ? `?services=${ids}` : ""
            this.props.navigate(`/organization/${this.state.organization.url || this.state.organization.id}${query}`)
        }
        else {
            this.props.navigate(-1)
        }
    }

    /* Draw Ask Time — the hour is not picked: the hours are brought into view and lit up.
       On a phone they stand far above the button, and a button which silently does nothing
       reads as a broken one */
    _askTime = () => {
        this.setState({ timeMissing: true }, () => {
            if (this._times.current) {
                this._times.current.scrollIntoView({ behavior: "smooth", block: "center" })
            }
        })
    }

    /* Draw Submit */
    _submit = () => {
        const organization = this.state.organization

        /* Check — the queue takes no hour, a booking does */
        if (!organization.queue && !this.state.time) {
            this._askTime()
            return
        }

        this.setState({ submitting: true, submitError: null })

        if (organization.queue) {
            join(this.props.token, { organizationId: organization.id, serviceIds: this._serviceIds() }).then((response) => {
                if (this._sessionExpired(response)) {
                    return
                }
                const submitError = this._submitError(response)
                this.setState({ submitting: false, submitError })
                if (!submitError) {
                    this.props.navigate("/bookings")
                }
            })
            return
        }

        /* Booking channel carried from a share/QR/2gis link via ?source= */
        const source = new URLSearchParams(this.props.location.search).get("source")

        create(this.props.token, {
            organizationId: organization.id,
            serviceIds: this._serviceIds(),
            specialistId: this.state.specialist,
            date: this._dates()[this.state.dateIdx].iso,
            time: this.state.time,
            channel: source || "web"
        }).then((response) => {
            if (this._sessionExpired(response)) {
                return
            }
            const submitError = this._submitError(response)
            this.setState({ submitting: false, submitError })
            if (!submitError) {
                this.setState({ success: true })
                return
            }
            /* A taken slot is stale data — refresh the times so the friend can pick another */
            if (response && response.status === 409) {
                this.availability()
            }
        })
    }

    /* Session expired mid-booking — open the login modal in place and stop; returns true if handled.
       The banner stays behind the login modal so the reason is still on screen if the client closes
       it: without it the button just resets and the booking looks like it failed for no reason. */
    _sessionExpired = (response) => {
        if (response && (response.status === 401 || response.status === 498)) {
            this.setState({ submitting: false, submitError: "Сессия истекла. Войдите ещё раз и нажмите «Записаться»." })
            this.props.toggleAuth(true)
            return true
        }
        return false
    }

    render = () => {
        if (this.state.loading) {
            return (
                <div className="modal-backdrop">
                    <div className="modal modal--lg"><div className="modal__body"><Loading /></div></div>
                </div>
            )
        }
        if (this.state.error || !this.state.organization) {
            return (
                <div className="modal-backdrop" onClick={this._close}>
                    <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}><div className="modal__body"><Network onRetry={this.load} /></div></div>
                </div>
            )
        }

        const s = this.state.organization

        /* Success — same confirmation modal as the quick-book flow */
        if (this.state.success) {
            return (
                <div className="modal-backdrop" onClick={() => this.props.navigate("/bookings")}>
                    <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal__header">
                            <h3 className="modal__title">{translate("Новая запись")}</h3>
                            <button className="modal__close" onClick={() => this.props.navigate("/bookings")} aria-label={translate("Закрыть")}><Icon name="close" size={20} /></button>
                        </div>
                        <div className="modal__body" style={{ textAlign: "center", padding: "48px 28px" }}>
                            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--success)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                                <Icon name="check" size={36} />
                            </div>
                            <h3 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 8px" }}>{s.queue ? translate("Вы в очереди!") : translate("Запись создана!")}</h3>
                            <p style={{ color: "var(--muted)", margin: "0 auto 28px", maxWidth: 340 }}>
                                {s.queue ? `${translate("Ждём вас в")} ${s.name}. ${translate("Уведомим, когда подойдёт очередь.")}` : `${translate("Ждём вас в")} ${s.name}. ${translate("Напомним за час до записи.")}`}
                            </p>
                            <button className="btn btn--lg btn--block" onClick={() => this.props.navigate("/bookings")}>{translate("Отлично")}</button>
                        </div>
                    </div>
                </div>
            )
        }

        const total = this.state.services.reduce((sum, svc) => sum + utils.effectivePrice(svc), 0)
        /* Only the dates the master really has hours on stand in the strip */
        const dates = this._openDates()
        const week = this._dates()
        const selected = week[this.state.dateIdx] ? week[this.state.dateIdx].iso : ""

        return (
            <div className="modal-backdrop" onClick={this._close}>
                <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
                    <div className="modal__header">
                        <h3 className="modal__title">{translate("Новая запись")}</h3>
                        <button className="modal__close" onClick={this._close} aria-label={translate("Закрыть")}><Icon name="close" size={20} /></button>
                    </div>

                    <div className="modal__body">
                        <h2 style={{ fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>{s.name}</h2>
                        <div style={{ display: "flex", gap: 12, color: "var(--muted)", fontSize: 13, marginTop: 6, marginBottom: 20 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="pin" size={14} /> {s.addr}</span>
                        </div>

                        <div style={{ padding: "14px 16px", background: "var(--bg-soft)", borderRadius: 12, marginBottom: 28 }}>
                            {this.state.services.map((svc, i) => (
                                <div key={`${svc.id}`} style={{ display: "flex", justifyContent: "space-between", padding: i > 0 ? "8px 0 0" : 0 }}>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{svc.name}</div>
                                        <div style={{ color: "var(--muted)", fontSize: 13 }}>{svc.duration}</div>
                                    </div>
                                    <div style={{ fontWeight: 800 }}>
                                        {utils.price(svc)}
                                        {utils.hasDiscount(svc) && <span style={{ textDecoration: "line-through", color: "var(--muted)", fontWeight: 400, marginLeft: 6 }}>{utils.money(svc.price)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {!s.queue && this.state.specialists.length > 1 && (
                            <div style={{ marginBottom: 28 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Выберите специалиста</h4>
                                <div className="spec-grid">
                                    {this.state.specialists.map((sp) => (
                                        <button key={`${sp.id}`} className={`spec ${this.state.specialist === sp.id ? "is-selected" : ""}`} onClick={() => this.setState({ specialist: sp.id, dateIdx: 0, time: null }, this.availability)}>
                                            <span className="spec__check"><Icon name="check" size={14} /></span>
                                            <div className="spec__avatar">{sp.avatar ? <img src={utils.media(sp.avatar)} alt="" onError={utils.onAvatarError} /> : <Icon name="user" size={36} />}</div>
                                            <div className="spec__name">{sp.name}</div>
                                            {sp.profession && <div className="spec__profession">{sp.profession}</div>}
                                            {sp.rating > 0 && <div className="spec__rating"><Icon name="star" size={12} fill="currentColor" stroke="none" /> {Number(sp.rating).toFixed(2)}</div>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {s.queue ? (
                            <div className="queue-note">
                                <span className="queue-note__num">{s.queueCount || 0}</span>
                                <div>
                                    <div className="queue-note__label">человек в живой очереди</div>
                                    <div className="queue-note__wait"><Icon name="clock" size={13} /> Время не выбирается — встаньте в очередь, и мы уведомим, когда подойдёт ваш черёд</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{ marginBottom: 28 }}>
                                    <h4 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Выберите дату</h4>
                                    {dates.length === 0 ? this._noDates() : (
                                        <div className="date-strip">
                                            {dates.map((d) => (
                                                <button key={d.iso} className={`date ${selected === d.iso ? "is-selected" : ""}`} onClick={() => this._pickDate(d)}>
                                                    <div className="date__dow">{d.dow}</div>
                                                    <div className="date__day">{d.day}</div>
                                                    <div className="date__mon">{d.mon}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* A week without a single free hour says so once, above — a
                                    second empty block under it would only repeat the same thing */}
                                {dates.length > 0 && (
                                    <div ref={this._times} className={this.state.timeMissing ? "times-block is-missing" : "times-block"}>
                                        <h4 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Выберите время</h4>
                                        {this.state.timeMissing && <div className="times-hint">Выберите время, чтобы записаться</div>}
                                        <div className="times">
                                            {this.state.slots.map((slot) => (
                                                <button key={slot.time} className={`time ${this.state.time === slot.time ? "is-selected" : ""}`} disabled={!slot.available} onClick={() => this.setState({ time: slot.time, timeMissing: false })}>
                                                    {slot.time}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {this.state.submitError && (
                        <div style={{ padding: "12px 16px", margin: "0 20px", background: "var(--danger-bg)", color: "var(--danger)", borderRadius: 12, fontSize: 14, fontWeight: 600 }}>
                            {this.state.submitError}
                        </div>
                    )}
                    <div className="modal__footer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                        <div>
                            <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Итого</div>
                            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>от {utils.money(total)}</div>
                        </div>
                        {/* Without an hour the button names the next step and leads down to the
                            hours — a pale «Записаться» told the client nothing, and nobody guessed
                            the hours stood further down the page */}
                        <button className="btn btn--lg" style={{ padding: "14px 36px" }} disabled={this.state.submitting} onClick={this._submit}>
                            {s.queue ? "Встать в очередь" : (this.state.time ? "Записаться" : "Выбрать время")}
                            <Icon name={s.queue || this.state.time ? "arrow_right" : "chevron_down"} size={16} />
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Booking))
